import * as React from 'react';
import { useEffect, useState, useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { Spinner } from '@fluentui/react-components';

// service
import * as PaymentService from '../../../../services/CashFlow-Payments.Services';
import { Payment } from '../../../../models/Entities/CashFlow/Payment/Payment';
import { useSelector } from 'react-redux';
import { AppState } from '../../../../redux/reducers';

import './Payments.Graph.Styles.css';

// =========================================================
// Palette per currency (income / outcome)
// =========================================================
type CurrencyColors = { income: string; outcome: string };
const CURRENCY_PALETTE: Record<string, CurrencyColors> = {
  ARS: { income: '#247dbd', outcome: '#bf482b' },
  USD: { income: '#16a34a', outcome: '#ea580c' },
  EUR: { income: '#7c3aed', outcome: '#db2777' },
  BRL: { income: '#0891b2', outcome: '#d97706' },
  CLP: { income: '#0d9488', outcome: '#c2410c' },
  UYU: { income: '#0369a1', outcome: '#b91c1c' },
};
const FALLBACK_PAIRS: CurrencyColors[] = [
  { income: '#0e7490', outcome: '#e11d48' },
  { income: '#9333ea', outcome: '#f59e0b' },
  { income: '#65a30d', outcome: '#be123c' },
  { income: '#0284c7', outcome: '#c026d3' },
  { income: '#15803d', outcome: '#d97706' },
];
const NET_COLOR = '#20ad9b';
const NET_NEG_COLOR = '#cf3a3a';

const colorsForCurrency = (code: string, idx: number): CurrencyColors => {
  const fallback = FALLBACK_PAIRS[idx % FALLBACK_PAIRS.length];
  return CURRENCY_PALETTE[code.toUpperCase()] ?? fallback;
};

// =========================================================
// Date helpers
// =========================================================
type GroupBy = 'month' | 'year';

const buildCategories = (start: Date, end: Date, groupBy: GroupBy): string[] => {
  const cats: string[] = [];
  if (groupBy === 'year') {
    for (let y = start.getFullYear(); y <= end.getFullYear(); y++) cats.push(`${y}`);
    return cats;
  }
  const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
  const limit = new Date(end.getFullYear(), end.getMonth(), 1);
  while (cursor <= limit) {
    cats.push(`${cursor.getFullYear()}-${(cursor.getMonth() + 1).toString().padStart(2, '0')}`);
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return cats;
};

const keyForDate = (d: Date, groupBy: GroupBy): string =>
  groupBy === 'year'
    ? `${d.getFullYear()}`
    : `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}`;

// =========================================================
// Types
// =========================================================
interface CurrencyBucket {
  income: Map<string, number>;
  outcome: Map<string, number>;
}

interface CurrencySeries {
  currency: string;
  income: number[];
  outcome: number[];
  net: number[];
  totalIncome: number;
  totalOutcome: number;
  totalNet: number;
  margin: number;
}

type ViewMode = 'bars' | 'lines' | 'area';

// =========================================================
// Per-currency chart builder
// =========================================================
const buildOptionForCurrency = (
  cs: CurrencySeries,
  categories: string[],
  colors: CurrencyColors,
  viewMode: ViewMode,
  groupBy: GroupBy,
  locale: string,
  sliderExtraGap: number
) => {

  const incomeName = `Ingresos ${cs.currency}`;
  const outcomeName = `Egresos ${cs.currency}`;
  const netName = `Neto ${cs.currency}`;

  const baseBar = (name: string, color: string, data: number[]) => ({
    name,
    type: 'bar' as const,
    barMaxWidth: 36,
    itemStyle: {
      borderRadius: [4, 4, 0, 0],
      color: {
        type: 'linear',
        x: 0, y: 0, x2: 0, y2: 1,
        colorStops: [
          { offset: 0, color },
          { offset: 1, color: `${color}AA` }
        ]
      }
    },
    emphasis: { focus: 'series', itemStyle: { color } },
    data
  });

  const baseLine = (name: string, color: string, data: number[], extra: Record<string, any> = {}) => ({
    name,
    type: 'line' as const,
    smooth: true,
    showSymbol: false,
    lineStyle: { width: 2.5, color },
    itemStyle: { color },
    emphasis: { focus: 'series', lineStyle: { width: 3.5 } },
    data,
    ...extra
  });

  const baseArea = (name: string, color: string, data: number[]) => ({
    name,
    type: 'line' as const,
    smooth: true,
    showSymbol: false,
    lineStyle: { width: 2, color },
    itemStyle: { color },
    areaStyle: {
      color: {
        type: 'linear',
        x: 0, y: 0, x2: 0, y2: 1,
        colorStops: [
          { offset: 0, color: `${color}55` },
          { offset: 1, color: `${color}05` }
        ]
      }
    },
    emphasis: { focus: 'series' },
    data
  });

  const netLine = baseLine(netName, NET_COLOR, cs.net, {
    lineStyle: { width: 2, color: NET_COLOR, type: 'dashed' },
    z: 5,
    markLine: {
      symbol: 'none',
      silent: true,
      lineStyle: { color: '#cbd5e1', type: 'solid', width: 1 },
      data: [{ yAxis: 0 }]
    }
  });

  let series: any[];
  if (viewMode === 'bars') {
    series = [
      baseBar(incomeName, colors.income, cs.income),
      baseBar(outcomeName, colors.outcome, cs.outcome),
      netLine
    ];
  } else if (viewMode === 'lines') {
    series = [
      baseLine(incomeName, colors.income, cs.income),
      baseLine(outcomeName, colors.outcome, cs.outcome),
      netLine
    ];
  } else {
    series = [
      baseArea(incomeName, colors.income, cs.income),
      baseArea(outcomeName, colors.outcome, cs.outcome),
      netLine
    ];
  }

  const formatAmount = (v: number) =>
    v.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return {
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: viewMode === 'bars' ? 'shadow' : 'line', shadowStyle: { color: 'rgba(0,0,0,0.04)' } },
      backgroundColor: '#ffffff',
      borderColor: '#e4e7ec',
      borderWidth: 1,
      borderRadius: 10,
      padding: [10, 14],
      textStyle: { color: '#374151', fontSize: 12 },
      extraCssText: 'box-shadow: 0 4px 16px rgba(0,0,0,0.10);',
      formatter: (params: any[]) => {
        const label = params[0]?.axisValueLabel ?? params[0]?.name ?? '';
        const rows = params.map((p: any) => {
          const val = typeof p.value === 'number' ? formatAmount(p.value) : p.value;
          const isNet = p.seriesName === netName;
          const netColor = Number(p.value) >= 0 ? NET_COLOR : NET_NEG_COLOR;
          const valueColor = isNet ? netColor : '#111827';
          return `<div style="display:flex;align-items:center;gap:6px;padding:2px 0">
            <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${p.color}"></span>
            <span style="color:#697586">${p.seriesName}:</span>
            <b style="color:${valueColor}">${val} ${cs.currency}</b>
          </div>`;
        }).join('');
        return `<div style="font-size:12px"><div style="font-weight:600;margin-bottom:6px;color:#111827">${label}</div>${rows}</div>`;
      }
    },
    legend: {
      data: [incomeName, outcomeName, netName],
      type: 'scroll',
      top: 6,
      itemWidth: 12,
      itemHeight: 12,
      itemGap: 14,
      textStyle: { color: '#697586', fontSize: 12 }
    },
    grid: { left: 65, right: 20, top: 46, bottom: 56 + sliderExtraGap },
    xAxis: {
      type: 'category',
      boundaryGap: viewMode === 'bars',
      data: categories,
      axisLine: { lineStyle: { color: '#e4e7ec' } },
      axisTick: { show: false },
      axisLabel: {
        color: '#9ca3af',
        fontSize: 11,
        rotate: 35,
        formatter: (v: string) => {
          if (groupBy === 'year') return v;
          const [y, m] = v.split('-');
          const date = new Date(Number(y), Number(m) - 1, 1);
          return date.toLocaleDateString(locale, { month: 'short', year: '2-digit' });
        }
      }
    },
    yAxis: {
      type: 'value',
      splitLine: { lineStyle: { color: '#f3f4f6', type: 'dashed' } },
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: {
        color: '#9ca3af',
        fontSize: 11,
        formatter: (v: number) => {
          if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
          if (Math.abs(v) >= 1_000) return `${(v / 1_000).toFixed(0)}K`;
          return `${v}`;
        }
      }
    },
    toolbox: {
      feature: { saveAsImage: { title: `Guardar ${cs.currency}` }, restore: { title: 'Restablecer' } },
      right: 10,
      iconStyle: { borderColor: '#9ca3af' }
    },
    dataZoom: [
      { type: 'inside', throttle: 50 },
      {
        type: 'slider',
        height: 16,
        bottom: 8,
        borderColor: '#e4e7ec',
        fillerColor: 'rgba(36, 125, 189, 0.10)',
        handleStyle: { color: '#247dbd', borderColor: '#247dbd' },
        textStyle: { color: '#9ca3af', fontSize: 10 }
      }
    ],
    series
  };
};

// =========================================================
// Component
// =========================================================
export const PaymentsGraphComponent = (props: {
  startDate: Date;
  endDate: Date;
  entityIdentifier: string;
  relatedEntityIdentifier?: string | undefined;
  groupBy?: GroupBy;
  sliderExtraGap?: number;
  title?: string;
  subtitle?: string;
  defaultViewMode?: ViewMode;
}) => {

  const authToken: string | undefined = useSelector((state: AppState) => state?.oidc?.user?.access_token);

  const [incomePayments, setIncomePayments] = useState<Payment[]>([]);
  const [outcomePayments, setOutcomePayments] = useState<Payment[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>(props.defaultViewMode ?? 'bars');
  const [activeCurrency, setActiveCurrency] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!authToken) return;
      setIsLoading(true);
      try {
        const [incomeFetched, outcomeFetched] = await Promise.all([
          PaymentService.GetPaymentsByReceiver(authToken, props.entityIdentifier, props.relatedEntityIdentifier),
          PaymentService.GetPaymentsByPayer(authToken, props.entityIdentifier, props.relatedEntityIdentifier)
        ]);
        setIncomePayments(incomeFetched ?? []);
        setOutcomePayments(outcomeFetched ?? []);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [authToken, props.entityIdentifier, props.relatedEntityIdentifier]);

  const groupBy: GroupBy = props.groupBy || 'month';

  const categories = useMemo(
    () => buildCategories(new Date(props.startDate), new Date(props.endDate), groupBy),
    [props.startDate, props.endDate, groupBy]
  );

  const perCurrency: CurrencySeries[] = useMemo(() => {
    const startMs = new Date(props.startDate).getTime();
    const endMs = new Date(props.endDate).getTime();
    const map = new Map<string, CurrencyBucket>();

    const accumulate = (payments: Payment[], type: 'income' | 'outcome') => {
      payments.forEach(p => {
        const t = new Date(p.paymentDate).getTime();
        if (Number.isNaN(t) || t < startMs || t > endMs) return;
        const ccy = (p.currencyCode || 'N/A').toUpperCase();
        const k = keyForDate(new Date(p.paymentDate), groupBy);
        let bucket = map.get(ccy);
        if (!bucket) {
          bucket = { income: new Map(), outcome: new Map() };
          map.set(ccy, bucket);
        }
        const target = bucket[type];
        target.set(k, (target.get(k) || 0) + Number(p.amount || 0));
      });
    };

    accumulate(incomePayments, 'income');
    accumulate(outcomePayments, 'outcome');

    const codes = Array.from(map.keys()).sort((a, b) => a.localeCompare(b));
    return codes.map(ccy => {
      const bucket = map.get(ccy);
      const income = categories.map(c => Number((bucket?.income.get(c) ?? 0).toFixed(2)));
      const outcome = categories.map(c => Number((bucket?.outcome.get(c) ?? 0).toFixed(2)));
      const net = income.map((v, i) => Number((v - outcome[i]).toFixed(2)));
      const totalIncome = income.reduce((a, b) => a + b, 0);
      const totalOutcome = outcome.reduce((a, b) => a + b, 0);
      const totalNet = totalIncome - totalOutcome;
      const margin = totalIncome > 0 ? (totalNet / totalIncome) * 100 : 0;
      return { currency: ccy, income, outcome, net, totalIncome, totalOutcome, totalNet, margin };
    });
  }, [incomePayments, outcomePayments, categories, props.startDate, props.endDate, groupBy]);

  const locale = window.navigator.language || 'es-AR';
  const sliderExtraGap = props.sliderExtraGap ?? 20;

  const fmt = (v: number) =>
    v.toLocaleString(locale, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

  const chartHeight = 320;

  // Auto-select first currency (or keep current if still present)
  useEffect(() => {
    if (!perCurrency.length) {
      if (activeCurrency !== null) setActiveCurrency(null);
      return;
    }
    const stillExists = activeCurrency && perCurrency.some(c => c.currency === activeCurrency);
    if (!stillExists) setActiveCurrency(perCurrency[0].currency);
  }, [perCurrency, activeCurrency]);

  const selected = useMemo(
    () => perCurrency.find(c => c.currency === activeCurrency) ?? perCurrency[0] ?? null,
    [perCurrency, activeCurrency]
  );
  const selectedIdx = selected ? perCurrency.findIndex(c => c.currency === selected.currency) : -1;

  const headerSubtitle = useMemo(() => {
    if (props.subtitle) return props.subtitle;
    if (!perCurrency.length) return 'Ingresos y egresos por moneda en el período seleccionado';
    const count = perCurrency.length;
    return `${count} ${count === 1 ? 'moneda' : 'monedas'} en el período seleccionado`;
  }, [props.subtitle, perCurrency]);

  return (
    <div className='paymentsGraph'>

      {isLoading && (
        <div className='paymentsGraph__loadingOverlay'>
          <Spinner size='medium' />
        </div>
      )}

      {/* Header */}
      <div className='paymentsGraph__header'>
        <div className='paymentsGraph__headerLeft'>
          <span className='paymentsGraph__title'>
            {props.title ?? 'Movimientos de caja'}
          </span>
          <span className='paymentsGraph__subtitle'>{headerSubtitle}</span>
        </div>

        <div className='paymentsGraph__viewToggle' aria-label='Tipo de visualización'>
          <button
            type='button'
            className={`paymentsGraph__viewToggleBtn${viewMode === 'bars' ? ' is-active' : ''}`}
            onClick={() => setViewMode('bars')}
          >
            Barras
          </button>
          <button
            type='button'
            className={`paymentsGraph__viewToggleBtn${viewMode === 'lines' ? ' is-active' : ''}`}
            onClick={() => setViewMode('lines')}
          >
            Líneas
          </button>
          <button
            type='button'
            className={`paymentsGraph__viewToggleBtn${viewMode === 'area' ? ' is-active' : ''}`}
            onClick={() => setViewMode('area')}
          >
            Área
          </button>
        </div>
      </div>

      {/* Empty state */}
      {!isLoading && perCurrency.length === 0 && (
        <div className='paymentsGraph__empty'>
          <div className='paymentsGraph__emptyIcon'>∅</div>
          <div className='paymentsGraph__emptyTitle'>Sin movimientos en el período</div>
          <div className='paymentsGraph__emptySubtitle'>
            No se registraron ingresos ni egresos entre las fechas seleccionadas.
          </div>
        </div>
      )}

      {/* Currency tabs (only if more than one) */}
      {perCurrency.length > 1 && (
        <div className='paymentsGraph__currencyTabs' aria-label='Monedas'>
          {perCurrency.map((cs, idx) => {
            const colors = colorsForCurrency(cs.currency, idx);
            const isActive = cs.currency === selected?.currency;
            const netPositive = cs.totalNet >= 0;
            return (
              <button
                key={cs.currency}
                type='button'
                className={`paymentsGraph__currencyTab${isActive ? ' is-active' : ''}`}
                onClick={() => setActiveCurrency(cs.currency)}
                style={isActive ? { borderBottomColor: colors.income } : undefined}
              >
                <span className='paymentsGraph__currencyTabBadge'
                      style={{ background: `linear-gradient(135deg, ${colors.income} 0%, ${colors.outcome} 100%)` }}>
                  {cs.currency}
                </span>
                <span className='paymentsGraph__currencyTabNet'
                      style={{ color: netPositive ? NET_COLOR : NET_NEG_COLOR }}>
                  {netPositive ? '+' : ''}{fmt(cs.totalNet)}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* Selected currency panel */}
      {selected && (() => {
        const colors = colorsForCurrency(selected.currency, Math.max(selectedIdx, 0));
        const netPositive = selected.totalNet >= 0;
        const option = buildOptionForCurrency(selected, categories, colors, viewMode, groupBy, locale, sliderExtraGap);

        return (
          <section className='paymentsGraph__currencyPanel paymentsGraph__currencyPanel--flat' key={selected.currency}>

            <div className='paymentsGraph__kpiRow'>
              <div className='paymentsGraph__kpi paymentsGraph__kpi--income'
                   style={{ borderColor: `${colors.income}55` }}>
                <span className='paymentsGraph__kpiLabel'>Ingresos {selected.currency}</span>
                <span className='paymentsGraph__kpiValue' style={{ color: colors.income }}>
                  {fmt(selected.totalIncome)}
                </span>
              </div>
              <div className='paymentsGraph__kpi paymentsGraph__kpi--outcome'
                   style={{ borderColor: `${colors.outcome}55` }}>
                <span className='paymentsGraph__kpiLabel'>Egresos {selected.currency}</span>
                <span className='paymentsGraph__kpiValue' style={{ color: colors.outcome }}>
                  {fmt(selected.totalOutcome)}
                </span>
              </div>
              <div className={`paymentsGraph__kpi ${netPositive ? 'paymentsGraph__kpi--profit' : 'paymentsGraph__kpi--loss'}`}>
                <span className='paymentsGraph__kpiLabel'>Neto</span>
                <span className='paymentsGraph__kpiValue'
                      style={{ color: netPositive ? NET_COLOR : NET_NEG_COLOR }}>
                  {netPositive ? '+' : ''}{fmt(selected.totalNet)}
                </span>
              </div>
              <div className={`paymentsGraph__kpi ${netPositive ? 'paymentsGraph__kpi--profit' : 'paymentsGraph__kpi--loss'}`}>
                <span className='paymentsGraph__kpiLabel'>Margen</span>
                <span className='paymentsGraph__kpiValue'
                      style={{ color: netPositive ? NET_COLOR : NET_NEG_COLOR }}>
                  {selected.margin.toFixed(1)}%
                </span>
              </div>
            </div>

            <div className='paymentsGraph__chartWrapper'>
              <ReactECharts
                notMerge={true}
                lazyUpdate={true}
                option={option}
                style={{ height: `${chartHeight}px`, width: '100%' }}
              />
            </div>
          </section>
        );
      })()}

    </div>
  );
};
